/**
 * useThreadFilePolicy — the server's upload rules for an entry's content
 * thread: the types that may be attached, the per-file and per-frame byte
 * limits, and how many files one message may carry. The composer's checks
 * read these, so the client and the server can never disagree on them.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { ThreadFilePolicy } from '@discovr/types';

import { fetchThreadFilePolicy } from '../api/threadFiles';
import { campaignKeys } from '../queryKeys';

/** A policy only changes with a server release, so one fetch serves a long session. */
const FILE_POLICY_STALE_MS = 60 * 60_000;

export interface UseThreadFilePolicyOptions {
  /** `false` holds the fetch — e.g. for a viewer who can't attach anything. Defaults to true. */
  enabled?: boolean;
}

/** GET /api/v1/roster/{entry_id}/threads/content/file-policy, cached per entry. Idle without an entry id. */
export function useThreadFilePolicy(entryId: string, options?: UseThreadFilePolicyOptions): UseQueryResult<ThreadFilePolicy> {
  return useQuery<ThreadFilePolicy>({
    queryKey: campaignKeys.threadFilePolicy(entryId),
    queryFn: ({ signal }) => fetchThreadFilePolicy(entryId, signal),
    enabled: !!entryId && (options?.enabled ?? true),
    staleTime: FILE_POLICY_STALE_MS,
  });
}
